import {
    BUILTIN_VIEW_ID,
    type ResolvableViewRow,
    resolveActiveViewIdFromRows,
} from '@client/components/table-views/resolve-active-view';
import { SAVED_VIEW_QUERY_OPTIONS } from '@client/components/table-views/saved-view-query-options';
import { getPreviousPathname, isReturnFromSubRoute } from '@client/hooks/navigation/previous-pathname';
import { getPathStateKey } from '@client/hooks/navigation/usePathState';
import { usePathStateStore } from '@client/stores/PathStateStore';
import { canPrefetchFreshListInput } from './fresh-list-inputs';

/**
 * `beforeLoad` prefetch planning for saved-view list pages (claims/complaints/incidents),
 * the list-page counterpart of `detail-route-prefetch.ts` (CUR-1036, task 2.2).
 *
 * The mounted page's list query is gated behind `useTableViews`'s one-shot view
 * resolution (`viewsReady`), which itself waits on `savedView.listSavedViews` — a
 * serial chain that cannot start before React mounts. This module starts the same
 * requests from `beforeLoad` instead, but ONLY when the list input the page will use
 * is exactly computable without running the view-resolution state machine:
 *
 * - **Drill-down return** (path state holds `query-params`): the page restores and
 *   queries those params verbatim, even if the original deep-link URL remains.
 * - **Fresh explicit deep link**: filters/`?view` take precedence over stale stored
 *   state, matching the mounted filter/view state machines, so guessed prefetches skip.
 * - **Fresh default entry** (no explicit deep link and no default saved view): the
 *   page's shared, settled built-in input is exact — prefetch that.
 * - Anything else (deep-link filters, `?view`, a default saved/built-in view) needs
 *   the filter config + reconcile pipeline, so the plan says skip: prefetching a
 *   guessed input would add a wasted request without warming the right cache entry.
 *   The saved-views round trip itself is still warmed for every case.
 */

export type ListPrefetchPlan<TInput> =
    | { kind: 'stored'; input: TInput }
    | { kind: 'default'; input: TInput }
    | { kind: 'skip'; reason: 'deep-link-filters' | 'view-deep-link' | 'non-default-view' };

/**
 * Decide whether the list query's input is exactly known pre-mount. Pure — unit-tested
 * against the resolution rules `useTableViews` applies after mount.
 */
export function resolveListPrefetchPlan<TInput>({
    storedQueryParams,
    urlFilterCount,
    urlViewId,
    savedViewRows,
    returnedFromSubRoute,
    defaultInput,
}: {
    storedQueryParams: TInput | undefined;
    urlFilterCount: number;
    urlViewId: string | undefined;
    savedViewRows: readonly ResolvableViewRow[];
    returnedFromSubRoute: boolean;
    defaultInput: TInput;
}): ListPrefetchPlan<TInput> {
    // Explicit entry instructions win over stale path state on a fresh deep link.
    // A genuine drill-down return is the exception: the URL still carries the
    // original instruction, while the stored list state contains the user's work.
    if (!returnedFromSubRoute && urlFilterCount > 0) {
        return { kind: 'skip', reason: 'deep-link-filters' };
    }
    if (!returnedFromSubRoute && urlViewId !== undefined) {
        return { kind: 'skip', reason: 'view-deep-link' };
    }
    if (storedQueryParams !== undefined) {
        return { kind: 'stored', input: storedQueryParams };
    }
    if (urlFilterCount > 0) return { kind: 'skip', reason: 'deep-link-filters' };
    if (urlViewId !== undefined) return { kind: 'skip', reason: 'view-deep-link' };
    const resolved = resolveActiveViewIdFromRows(savedViewRows, undefined, undefined, undefined);
    return resolved === BUILTIN_VIEW_ID
        ? { kind: 'default', input: defaultInput }
        : { kind: 'skip', reason: 'non-default-view' };
}

/** The stored `query-params` path state for a pathname, read outside React. */
export function readStoredQueryParams<TInput>(pathname: string): TInput | undefined {
    return usePathStateStore.getState().getState(getPathStateKey(pathname, 'query-params'))?.value as
        | TInput
        | undefined;
}

/**
 * The saved-views warm uses the exact shared cache options (`SAVED_VIEW_QUERY_OPTIONS`,
 * also used by `useTableViews` and the login warm-up, so freshness rules cannot drift)
 * plus the speculative-fetch error meta from `DETAIL_RECORD_PREFETCH_OPTIONS`.
 */
const SAVED_VIEW_PREFETCH_OPTIONS = {
    ...SAVED_VIEW_QUERY_OPTIONS,
    meta: { errorSurface: 'silent', captureToSentry: false },
} as const;

interface SavedViewsUtilsLike<TEntityType extends string> {
    prefetch: (input: { entityType: TEntityType }, opts: typeof SAVED_VIEW_PREFETCH_OPTIONS) => Promise<unknown>;
    getData: (input: { entityType: TEntityType }) => { rows: readonly ResolvableViewRow[] } | undefined;
}

/**
 * Run the list-page prefetches for one route. Call inside the deferred-auth callback
 * (`runDetailRoutePrefetchWhenAuthorised`) from the route's `beforeLoad`.
 *
 * Warms `listSavedViews` regardless of the synchronous feature-flag state (the
 * procedure is safe and returns no rows when none exist), and starts the list query
 * when settings have hydrated and the plan resolves — synchronously off the login
 * warm-up's cache when it's already populated, otherwise after the views fetch lands.
 * Failures fall through to the render-time `useQuery`; everything here only warms
 * caches.
 */
export function runListRoutePrefetch<TInput, TEntityType extends string>({
    savedViewsUtils,
    entityType,
    pathname,
    urlFilterCount,
    urlViewId,
    defaultInput,
    prefetchList,
}: {
    savedViewsUtils: SavedViewsUtilsLike<TEntityType>;
    entityType: TEntityType;
    pathname: string;
    urlFilterCount: number;
    urlViewId: string | undefined;
    defaultInput: TInput;
    prefetchList: (input: TInput) => void;
}): void {
    const returnedFromSubRoute = isReturnFromSubRoute(getPreviousPathname(), pathname);
    // Capture readiness before any asynchronous saved-view work. If settings are
    // still hydrating, `defaultInput` was built with the fallback page size and
    // must not be prefetched later after the saved settings have arrived.
    const canPrefetchList = canPrefetchFreshListInput();

    const decideAndPrefetch = (rows: readonly ResolvableViewRow[]): void => {
        const plan = resolveListPrefetchPlan({
            storedQueryParams: readStoredQueryParams<TInput>(pathname),
            urlFilterCount,
            urlViewId,
            savedViewRows: rows,
            returnedFromSubRoute,
            defaultInput,
        });
        if (plan.kind !== 'skip') {
            prefetchList(plan.input);
        }
    };

    const cached = savedViewsUtils.getData({ entityType });
    // Warm the round trip `useTableViews` will otherwise block on. `prefetch` respects
    // the shared staleTime, so a fresh login warm-up (`useWarmSavedViews`) makes this a
    // no-op instead of a duplicate request.
    const viewsPrefetch = savedViewsUtils.prefetch({ entityType }, SAVED_VIEW_PREFETCH_OPTIONS);
    if (!canPrefetchList) {
        return;
    }
    if (cached) {
        decideAndPrefetch(cached.rows);
        return;
    }
    void viewsPrefetch
        .then(() => {
            const data = savedViewsUtils.getData({ entityType });
            if (data === undefined) return;
            decideAndPrefetch(data.rows);
        })
        .catch(() => {
            // Views fetch failed — the render-time query owns retries; nothing to warm.
        });
}
